/**
 * Campaign Spend tracking component.
 *
 * Lists, adds, and deletes real spend entries via /api/revenue/spend.
 * Per-campaign ROAS uses the channel breakdown's attributed revenue for the
 * entry's channel over the spend period; when the channel has no attributed
 * revenue the cell shows a dash rather than an invented number.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure, type CampaignSpendEntry } from '../api';
import { Notice } from './Panels';
import { ImportRevenueDialog } from './ImportRevenueDialog';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
}

const CHANNELS = ['organic', 'paid', 'social', 'email', 'referral', 'direct', 'affiliate', 'other'];

const EMPTY_FORM = {
  campaign: '',
  channel: 'paid',
  provider: 'manual',
  amount: '',
  periodStart: '',
  periodEnd: '',
};

export function CampaignSpendTracker() {
  const [spend, setSpend] = useState<CampaignSpendEntry[]>([]);
  const [revenueByChannel, setRevenueByChannel] = useState<Map<string, number>>(new Map());
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [showImport, setShowImport] = useState(false);

  const loadSpend = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      const data = await api.listCampaignSpend();
      setSpend(data.spend);

      // Channel revenue for ROAS: cover the full span of spend entries.
      if (data.spend.length > 0) {
        const startDate = data.spend.reduce((min, s) => (s.periodStart < min ? s.periodStart : min), data.spend[0]!.periodStart);
        const endDate = data.spend.reduce((max, s) => (s.periodEnd > max ? s.periodEnd : max), data.spend[0]!.periodEnd);
        const channels = await api.getRevenueChannelBreakdown({ startDate, endDate });
        setRevenueByChannel(new Map(channels.breakdown.map((row) => [row.channel, row.revenue])));
      } else {
        setRevenueByChannel(new Map());
      }
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadSpend(); }, [loadSpend]);

  const addSpend = async () => {
    const amount = Number(formData.amount);
    if (formData.campaign.trim() === '' || formData.periodStart === '' || formData.periodEnd === '') {
      setFormError('Campaign, period start, and period end are required.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setFormError('Amount must be a positive number.');
      return;
    }
    if (formData.periodEnd < formData.periodStart) {
      setFormError('Period end is before period start — swap the dates.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      await api.createCampaignSpend({
        campaign: formData.campaign.trim(),
        channel: formData.channel,
        provider: formData.provider,
        amount,
        periodStart: formData.periodStart,
        periodEnd: formData.periodEnd,
      });
      setShowForm(false);
      setFormData(EMPTY_FORM);
      await loadSpend();
    } catch (error) {
      setFormError(error instanceof ApiFailure ? `${error.message} ${error.remedy}` : 'Request failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteSpend = async (id: string) => {
    setConfirmingId(null);
    try {
      await api.deleteCampaignSpend(id);
      setSpend((current) => current.filter((s) => s.id !== id));
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    }
  };

  return (
    <div className="page">
      <header className="intro">
        <h2>Campaign Spend</h2>
        <p>Track advertising spend to calculate ROAS, CAC, and campaign profitability.</p>
      </header>

      <div className="row">
        <button className="primary" onClick={() => { setShowForm(!showForm); setFormError(null); }}>
          {showForm ? 'Cancel' : '+ Add Campaign Spend'}
        </button>
      </div>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {showForm && (
        <section className="card">
          <header><h2>Add Campaign Spend</h2></header>
          <div className="controls" style={{ background: 'transparent', border: 'none', padding: 0 }}>
            <label className="field">
              <span>Campaign Name</span>
              <input type="text" value={formData.campaign} onChange={(e) => setFormData({ ...formData, campaign: e.target.value })} placeholder="Spring Sale 2026" />
            </label>
            <label className="field">
              <span>Channel</span>
              <select value={formData.channel} onChange={(e) => setFormData({ ...formData, channel: e.target.value })}>
                {CHANNELS.map((channel) => (
                  <option key={channel} value={channel}>{channel}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Provider</span>
              <select value={formData.provider} onChange={(e) => setFormData({ ...formData, provider: e.target.value })}>
                <option value="manual">Manual</option>
                <option value="google">Google Ads</option>
                <option value="facebook">Facebook/Meta</option>
                <option value="linkedin">LinkedIn</option>
                <option value="twitter">Twitter/X</option>
                <option value="tiktok">TikTok</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="field">
              <span>Amount (USD)</span>
              <input type="number" min="0" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="5000" />
            </label>
            <label className="field">
              <span>Period Start</span>
              <input type="date" value={formData.periodStart} onChange={(e) => setFormData({ ...formData, periodStart: e.target.value })} />
            </label>
            <label className="field">
              <span>Period End</span>
              <input type="date" value={formData.periodEnd} onChange={(e) => setFormData({ ...formData, periodEnd: e.target.value })} />
            </label>
          </div>
          {formError !== null && <p className="muted" role="alert" style={{ marginTop: 8 }}>{formError}</p>}
          <div className="row" style={{ marginTop: 16 }}>
            <button className="primary" onClick={() => void addSpend()} disabled={saving}>
              {saving ? <span className="spinner" /> : 'Add Spend'}
            </button>
          </div>
        </section>
      )}

      {loading && <div className="card"><p className="muted"><span className="spinner" /> Loading campaign spend…</p></div>}

      {!loading && spend.length === 0 && failure === null && (
        <div className="card empty">
          <h3>No campaign spend recorded</h3>
          <p>
            Add advertising spend above to calculate ROAS and campaign profitability against tracked revenue,
            or import spend history from an uploaded CSV.
          </p>
          <div className="row" style={{ justifyContent: 'center', marginTop: 12 }}>
            <button className="primary" onClick={() => setShowImport(true)}>
              Import from uploaded dataset
            </button>
          </div>
        </div>
      )}

      {showImport && (
        <ImportRevenueDialog
          initialMode="spend"
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); void loadSpend(); }}
        />
      )}

      {!loading && spend.length > 0 && (
        <section className="card">
          <header><h2>Campaign Spend</h2></header>
          <table className="data-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Channel</th>
                <th>Provider</th>
                <th style={{ textAlign: 'right' }}>Spend</th>
                <th style={{ textAlign: 'right' }}>ROAS</th>
                <th>Period</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {spend.map((s) => {
                const channelRevenue = s.channel !== null ? revenueByChannel.get(s.channel) : undefined;
                const roas = channelRevenue !== undefined && channelRevenue > 0 && s.amount > 0
                  ? channelRevenue / s.amount
                  : null;
                return (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{s.campaign}</td>
                    <td>{s.channel ?? '—'}</td>
                    <td>{s.provider}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(s.amount)}</td>
                    <td style={{ textAlign: 'right' }}>{roas !== null ? `${roas.toFixed(2)}x` : '—'}</td>
                    <td className="muted">{s.periodStart.slice(0, 10)} — {s.periodEnd.slice(0, 10)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {confirmingId === s.id ? (
                        <span className="row" style={{ justifyContent: 'flex-end' }}>
                          <button className="ghost" onClick={() => void deleteSpend(s.id)}>Confirm delete</button>
                          <button className="ghost" onClick={() => setConfirmingId(null)}>Cancel</button>
                        </span>
                      ) : (
                        <button className="ghost" onClick={() => setConfirmingId(s.id)}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="muted" style={{ marginTop: 8 }}>
            ROAS uses revenue attributed to the campaign's channel over its period; a dash means the channel has no attributed revenue yet.
          </p>
        </section>
      )}
    </div>
  );
}
